import React from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { SalesActivityTypeCount } from "./activitiesReportService";
import {
  CATEGORICAL_PALETTE,
  GRID_COLOR,
  renderTooltipCard,
  renderTooltipEntityHeader,
} from "./activityChartPalette";

interface ActivityTypeChartProps {
  types: SalesActivityTypeCount[];
  onSelectSegment: (typeId: number, statusId?: number) => void;
}

const ActivityTypeChart: React.FC<ActivityTypeChartProps> = ({
  types,
  onSelectSegment,
}) => {
  if (types.length === 0) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: "320px" }}
      >
        <div className="text-center">
          <i className="ti ti-chart-bar fs-1 text-muted mb-3" />
          <p className="text-muted mb-0">No activity data for the selected filters</p>
        </div>
      </div>
    );
  }

  // Each bar is one activity type, stacked by that type's own statuses —
  // so this chart answers "how much, AND what state is it in" in one view,
  // instead of just repeating the type totals the donut already shows.
  // Types don't share a status vocabulary (Call's "Answered" isn't
  // Meeting's "Done"), so segments are matched up by *position* within
  // each type's own status list, not by name — position 1 always gets the
  // same palette slot for visual rhythm across bars, but a shared legend
  // would misleadingly imply the statuses are the same thing, so real
  // names only ever show up in the direct label and the tooltip.
  const sortedTypes = [...types].sort((a, b) => b.counts - a.counts);
  const categories = sortedTypes.map((t) => t.crm_sales_activity_type_display_name);
  const maxStatusSlots = Math.max(1, ...sortedTypes.map((t) => t.statuses?.length || 0));

  const series = Array.from({ length: maxStatusSlots }, (_, position) => ({
    name: `slot-${position}`,
    data: sortedTypes.map((type) => type.statuses?.[position]?.counts ?? 0),
  }));

  const colors = Array.from(
    { length: maxStatusSlots },
    (_, i) => CATEGORICAL_PALETTE[i % CATEGORICAL_PALETTE.length]
  );

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 340,
      stacked: true,
      toolbar: { show: false },
      // These charts remount/update each other as a side effect of a
      // shared click-to-drill state (see ActivityStatusChart.tsx and
      // ActivityEntityBreakdownChart.tsx). ApexCharts' animation timers can
      // outlive an instance that gets torn down mid-animation in that
      // cascade and throw on the now-destroyed internals — disabling
      // animations removes that whole class of races.
      animations: { enabled: false },
      events: {
        dataPointSelection: (_event, _ctx, config) => {
          const type = sortedTypes[config.dataPointIndex];
          const status = type?.statuses?.[config.seriesIndex];
          if (!type) return;
          const typeId = type.crm_sales_activity_type_id;
          const statusId = status?.crm_sales_activity_type_status_id;
          // Deferred: selecting a type/status here remounts sibling charts
          // whose `key` depends on the selection. Doing that synchronously,
          // still inside ApexCharts' own click handling for *this* chart,
          // races that stack and throws.
          setTimeout(() => onSelectSegment(typeId, statusId), 0);
        },
      },
    },
    colors,
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        columnWidth: "48%",
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.25,
        opacityFrom: 1,
        opacityTo: 0.88,
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "11px", fontWeight: 700, colors: ["#fff"] },
      formatter: (val: number, opts) => {
        const type = sortedTypes[opts.dataPointIndex];
        const hasStatus = !!type?.statuses?.[opts.seriesIndex];
        return hasStatus && typeof val === "number" && val > 0 ? String(val) : "";
      },
    },
    // No stroke: a per-segment border is what was making each stacked
    // status read as a separate disconnected piece instead of one bar
    // that's just different colors along its length.
    stroke: { width: 0 },
    grid: {
      borderColor: GRID_COLOR,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { fontWeight: 600 } },
    },
    yaxis: {
      min: 0,
      labels: { formatter: (val: number) => Math.round(val).toString() },
    },
    legend: { show: false },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const type = sortedTypes[dataPointIndex];
        const status = type?.statuses?.[seriesIndex];
        if (!type || !status) return "";
        const swatch = w.globals.colors[seriesIndex];
        return renderTooltipCard(
          renderTooltipEntityHeader(
            status.counts,
            status.crm_sales_activity_type_status_display_name,
            type.crm_sales_activity_type_display_name,
            swatch
          )
        );
      },
    },
    states: {
      hover: { filter: { type: "darken" } },
    },
  };

  return (
    <>
      <Chart options={options} series={series} type="bar" height={340} />
      <p className="text-muted fs-12 mb-0 mt-2">
        <i className="ti ti-info-circle me-1" />
        Each bar is split by status — click a segment to drill into that exact type and status.
      </p>
    </>
  );
};

export default ActivityTypeChart;
